import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { type LayoutChangeEvent, useWindowDimensions } from "react-native";
import { Adapt, isWeb, Popover, type Popover as PopoverHandle, ScrollView, Sheet, type TamaguiElement, YStack } from "tamagui";
import { shadow } from "../../themes/token-shadow";
import { spaceTokens } from "../../themes/token-space";
import { useSafeAreaInsets } from "../../utils";
import { FIELD_FRAME_ATTR } from "../FieldShell/constants";
import { useNativeModalPortal } from "../Modal/NativeModalPortalContext";
import { presenceTransition } from "../Motion/motion";
import { SheetBackdrop } from "../SheetBackdrop";
import { SHEET_GRABBER_HEIGHT, SHEET_GRABBER_WIDTH } from "../sheetStyle";
import { useDropdownSurface } from "./DropdownSurfaceContext";
import { getReturnFocusTarget } from "./focusReturn";
import type { DropdownProps } from "./types";
import { useUsesSheet } from "./useInSheet";
const MIN_WIDTH = 200;
const ANCHOR_GAP = spaceTokens["sp0.5"];
const FRAME_PADDING = spaceTokens.sp1;
// Leaves a peek above the sheet so a long list scrolls internally, not off-screen.
const SHEET_SCREEN_TOP_GAP = spaceTokens.sp10;
// Fixed-height (`sheetFullHeight`) sheet detent, as a % of screen height.
const FULL_SHEET_PERCENT = 90;
type NativeAnchorRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};
export const getNativeAnchorRect = (measured: NativeAnchorRect, sheetPosition: number, anchorHeight?: number): NativeAnchorRect => ({
  ...measured,
  y: measured.y + sheetPosition,
  height: anchorHeight ?? measured.height
});
export const Dropdown = ({
  open,
  onOpenChange,
  trigger,
  anchor,
  returnFocusRef,
  children,
  sheetHeader,
  sheetFullHeight = false,
  matchTriggerWidth = true,
  align = "start",
  maxHeight,
  "aria-activedescendant": activeDescendant,
  contentLabel
}: DropdownProps) => {
  // Wraps the rendered trigger/anchor; used to measure the field frame and (trigger
  // mode) find the focusable to restore on close.
  const wrapperRef = useRef<TamaguiElement | null>(null);
  const popoverRef = useRef<PopoverHandle>(null);
  const resizeRafRef = useRef<number | null>(null);
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>(undefined);
  const [belowFrameOffset, setBelowFrameOffset] = useState(0);
  const [contentNode, setContentNode] = useState<HTMLElement | null>(null);
  const [spaceForContent, setSpaceForContent] = useState<number | undefined>(undefined);
  const insets = useSafeAreaInsets();
  const {
    height: windowHeight
  } = useWindowDimensions();
  const sheetScrollMaxHeight = windowHeight - insets.top - SHEET_SCREEN_TOP_GAP;
  const {
    nativePresentation,
    nativeAnchorHeight
  } = useDropdownSurface();
  const usesSheet = useUsesSheet(nativePresentation === "sheet");
  const nativeModalPortal = useNativeModalPortal();
  const sheetPosition = nativeModalPortal?.sheetPosition ?? 0;
  const registerNativeModalPortal = nativeModalPortal?.register;
  const dismissNativePopover = useCallback(() => onOpenChange(false), [onOpenChange]);
  useLayoutEffect(() => {
    if (isWeb || usesSheet || !open || !registerNativeModalPortal) {
      return;
    }
    return registerNativeModalPortal(dismissNativePopover);
  }, [dismissNativePopover, open, registerNativeModalPortal, usesSheet]);
  const measureAnchor = useCallback(() => {
    if (!isWeb) {
      if (!usesSheet) {
        wrapperRef.current?.measureInWindow((x, y, width, height) => {
          if (matchTriggerWidth) {
            setTriggerWidth(width);
          }
          popoverRef.current?.anchorTo(getNativeAnchorRect({
            x,
            y,
            width,
            height
          }, sheetPosition, nativeAnchorHeight));
        });
      }
      return;
    }
    const node = wrapperRef.current;
    if (!(node instanceof HTMLElement)) {
      return;
    }
    const field = node.firstElementChild;
    const widthAnchor = field instanceof HTMLElement ? field : node;
    if (matchTriggerWidth) {
      setTriggerWidth(widthAnchor.getBoundingClientRect().width);
    }
    // Anchor below the field frame, not the helper-text row. Frame depth varies by
    // field, so find it by FieldShell's attribute rather than depth or a CSS class.
    const frameEl = node.querySelector(`[${FIELD_FRAME_ATTR}]`);
    const frame = frameEl instanceof HTMLElement ? frameEl : widthAnchor;
    const belowFrame = node.getBoundingClientRect().bottom - frame.getBoundingClientRect().bottom;
    setBelowFrameOffset(Math.max(0, belowFrame));
  }, [matchTriggerWidth, nativeAnchorHeight, sheetPosition, usesSheet]);
  const handleWrapperLayout = useCallback((event: LayoutChangeEvent) => {
    if (!isWeb && matchTriggerWidth) {
      setTriggerWidth(event.nativeEvent.layout.width);
    }
  }, [matchTriggerWidth]);

  // Measure post-layout (width/anchor aren't known on first paint) and on each open.
  useLayoutEffect(() => {
    if (open) {
      measureAnchor();
    }
  }, [open, measureAnchor]);

  // Coalesce resize bursts to one measure per frame — each measure forces reflow.
  const handleResize = useCallback(() => {
    if (resizeRafRef.current !== null) {
      return;
    }
    resizeRafRef.current = requestAnimationFrame(() => {
      resizeRafRef.current = null;
      measureAnchor();
    });
  }, [measureAnchor]);
  useEffect(() => {
    if (!open || !isWeb) {
      return;
    }
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
      if (resizeRafRef.current !== null) {
        cancelAnimationFrame(resizeRafRef.current);
        resizeRafRef.current = null;
      }
    };
  }, [open, handleResize]);

  // jsdom lacks scrollIntoView, hence the optional call.
  useEffect(() => {
    if (!open || !isWeb || activeDescendant === undefined) {
      return;
    }
    document.getElementById(activeDescendant)?.scrollIntoView?.({
      block: "nearest"
    });
  }, [open, activeDescendant]);
  const handleOpenChange = useCallback((next: boolean) => {
    onOpenChange(next);
    // Trigger mode close-only: `Popover.Trigger` wraps a non-focusable YStack,
    // so restore focus to the caller's trigger. Anchor mode uses onCloseAutoFocus.
    if (!next && anchor === undefined && isWeb && wrapperRef.current instanceof HTMLElement) {
      getReturnFocusTarget(wrapperRef.current)?.focus({
        preventScroll: true
      });
    }
  }, [onOpenChange, anchor]);

  // Anchor mode: on close, focus the inner opener instead of letting Popover's
  // teardown chase the unmounting content down to <body>. Fires at focus-scope
  // teardown, so it wins the race without a timeout (not under jsdom — see below).
  const handleCloseAutoFocus = useCallback((event: Event) => {
    if (anchor === undefined) {
      return;
    }
    event.preventDefault();
    if (isWeb && returnFocusRef?.current instanceof HTMLElement) {
      returnFocusRef.current.focus({
        preventScroll: true
      });
    }
  }, [anchor, returnFocusRef]);

  // Anchor mode jsdom/programmatic-close fallback for `onCloseAutoFocus` above
  // (which never fires under jsdom, nor on a close that skips `onOpenChange`).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = wasOpenRef.current;
    wasOpenRef.current = open;
    if (wasOpen && !open && isWeb && returnFocusRef?.current instanceof HTMLElement) {
      returnFocusRef.current.focus({
        preventScroll: true
      });
    }
  }, [open, returnFocusRef]);
  const handleContentRef = useCallback((node: TamaguiElement | null) => {
    setContentNode(isWeb && node instanceof HTMLElement ? node : null);
  }, []);

  // Floating UI places and sizes the surface once at open and never observes later content growth.
  useEffect(() => {
    if (!open || !isWeb || usesSheet || contentNode === null) {
      return;
    }
    let frame: number | null = null;
    let repositionedForHeight: number | null = null;
    const measure = () => {
      frame = null;
      const content = contentNode.getBoundingClientRect();
      const anchorEl = wrapperRef.current;
      const anchorTop = anchorEl instanceof HTMLElement ? anchorEl.getBoundingClientRect().top : undefined;
      if (anchorTop !== undefined && content.top < anchorTop) {
        // Floating UI only re-places on reference layout, scroll and resize, so
        // a grown flipped surface needs one of those to re-anchor above.
        if (repositionedForHeight !== content.height) {
          repositionedForHeight = content.height;
          window.dispatchEvent(new Event("resize"));
        }
        setSpaceForContent(Math.max(0, anchorTop - ANCHOR_GAP - FRAME_PADDING));
        return;
      }
      setSpaceForContent(Math.max(0, window.innerHeight - FRAME_PADDING - content.top));
    };
    const schedule = () => {
      frame ??= requestAnimationFrame(measure);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(schedule);
    observer?.observe(contentNode);
    window.addEventListener("scroll", schedule, {
      capture: true,
      passive: true
    });
    window.addEventListener("resize", schedule);
    return () => {
      observer?.disconnect();
      window.removeEventListener("scroll", schedule, {
        capture: true
      });
      window.removeEventListener("resize", schedule);
      if (frame !== null) {
        cancelAnimationFrame(frame);
      }
      setSpaceForContent(undefined);
    };
  }, [open, usesSheet, contentNode]);
  const contentWidth = matchTriggerWidth && triggerWidth !== undefined ? triggerWidth : undefined;
  const scrollerMaxHeight = spaceForContent === undefined ? maxHeight : Math.min(maxHeight ?? Number.POSITIVE_INFINITY, spaceForContent);
  return <Popover ref={popoverRef} open={open} onOpenChange={handleOpenChange} placement={`bottom-${align}`} allowFlip stayInFrame={{
    padding: FRAME_PADDING
  }} resize={{
    padding: FRAME_PADDING
  }} offset={({
    placement
  }) => ({
    mainAxis: placement.startsWith("top") ? ANCHOR_GAP : ANCHOR_GAP - belowFrameOffset
  })} data-sentry-element="Popover" data-sentry-component="Dropdown" data-sentry-source-file="Dropdown.tsx">
      {anchor !== undefined ? <Popover.Anchor asChild>
          <YStack ref={wrapperRef} onLayout={handleWrapperLayout}>
            {anchor}
          </YStack>
        </Popover.Anchor> : <Popover.Trigger asChild>
          <YStack ref={wrapperRef} onLayout={handleWrapperLayout}>
            {trigger}
          </YStack>
        </Popover.Trigger>}

      {usesSheet ? <Adapt when="sm" platform="touch">
          <Sheet native={!sheetFullHeight} modal transition="overlay" dismissOnSnapToBottom snapPointsMode={sheetFullHeight ? "percent" : "fit"} snapPoints={sheetFullHeight ? [FULL_SHEET_PERCENT] : undefined}>
            <Sheet.Frame flex={sheetFullHeight ? 1 : undefined} paddingHorizontal="$sp1" backgroundColor="$surface1" borderTopLeftRadius="$br3" borderTopRightRadius="$br3" pointerEvents="auto">
              <YStack alignSelf="center" width={SHEET_GRABBER_WIDTH} height={SHEET_GRABBER_HEIGHT} marginTop="$sp2" marginBottom="$sp1" borderRadius={SHEET_GRABBER_HEIGHT / 2} backgroundColor="$bgDisabledSubtle" />
              {sheetHeader ? <YStack paddingTop="$sp2">{sheetHeader}</YStack> : null}
              <Sheet.ScrollView flex={sheetFullHeight ? 1 : undefined} maxHeight={sheetFullHeight ? undefined : sheetScrollMaxHeight} keyboardDismissMode="on-drag" keyboardShouldPersistTaps="handled" contentContainerStyle={{
            paddingBottom: insets.bottom + spaceTokens.sp1
          }}>
                <Adapt.Contents />
              </Sheet.ScrollView>
            </Sheet.Frame>
            <SheetBackdrop backgroundColor="$utilityOverlayDark" pointerEvents="auto"
        // The modal sheet collapses its root to zero height on close, which
        // would cut a fullscreen overlay off before it fades out.
        height={windowHeight} />
          </Sheet>
        </Adapt> : null}

      <Popover.Content ref={handleContentRef} onCloseAutoFocus={handleCloseAutoFocus} aria-label={contentLabel}
    // Native: keep the menu tappable above TrueSheet. Web: omit so Tamagui's
    // wrapper can drop presses during the exit fade — an explicit `auto`
    // compiles to `pointer-events: auto !important` and steals the next click.
    {...!isWeb ? {
      pointerEvents: "auto" as const
    } : {}} backgroundColor="$surface2" borderColor="$borderDefault" borderWidth={1} borderRadius="$br2"
    // Popover owns the horizontal inset here; the inner ScrollView owns vertical.
    paddingHorizontal="$sp1" paddingVertical="$sp0" zIndex="$modal" width={contentWidth} minWidth={MIN_WIDTH} overflow="hidden" opacity={1} enterStyle={{
      opacity: 0,
      scale: 0.96,
      y: -4
    }} exitStyle={{
      opacity: 0,
      scale: 0.96,
      y: -4
    }} transition={presenceTransition} animateOnly={isWeb ? undefined : ["opacity", "transform"]} {...shadow.medium} data-sentry-element="Popover.Content" data-sentry-source-file="Dropdown.tsx">
        <ScrollView
      // On web, fill + scroll within Popover.Content (whose height `resize`
      // shrinks near the edge). Native Popper measures intrinsic content, so a
      // flex child without a bounded parent would collapse to zero height.
      flex={isWeb && !usesSheet ? 1 : undefined} minHeight={isWeb && !usesSheet ? 0 : undefined}
      // Floating UI owns the available-height cap on web. Native Popper does not,
      // so it uses only the optional consumer cap.
      maxHeight={usesSheet ? undefined : isWeb ? scrollerMaxHeight : maxHeight} scrollEnabled={!usesSheet} keyboardShouldPersistTaps="handled" width="100%" contentContainerStyle={{
        width: "100%",
        paddingVertical: spaceTokens.sp1
      }} data-sentry-element="ScrollView" data-sentry-source-file="Dropdown.tsx">
          {children}
        </ScrollView>
      </Popover.Content>
    </Popover>;
};